import { connect, ConnectedProps } from 'react-redux';
import { ConfigurationFeatureFlag } from '@tink/config/src/tinkLinkConfig';
import { SkeletonLineLoader } from '@tink/core/src/components/atoms/SkeletonLineLoader/SkeletonLineLoader';
import { ProductType } from '@tink/core/src/types';
import { MarketCode } from '@tink/config/src/markets';
import { AppState } from '../../../stores/redux-store';
import {
  ConfigurationLoadingState,
  useConfiguration,
} from '../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';

import { setApplicationError as setApplicationErrorConnect } from '../../../stores/app-state-store';
import { ERR_USER_CANCELLED } from '../../../utilities/error';
import {
  InteractionElement,
  parentMessenger,
} from '../../../core/Analytics/ParentApplicationEventMessenger';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { useProduct } from '../../ApplicationProviders/ProductProvider/ProductProvider';
import { useUrlQuery } from '../../ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { applicationParameters } from '../../../utilities/application-parameters';
import { TINK_LOGO } from '../../../config';
import { BackButton, HeaderMolecule } from './HeaderMolecule';
import { HiddenHeader, StyledLoadingHeader } from './Header.styles';

export type HeaderProps = {
  pageView: PageView;
  addBottomMargin?: boolean;
  hideCloseButton?: boolean;
  hideLogo?: boolean;
} & BackButton;

export type HeaderPresenterProps = HeaderProps & PropsFromRedux;

export const hideGoBackButtonFunctionHack = () => undefined;

const hideGoBackButtonFunctionHackName = hideGoBackButtonFunctionHack.name;

export const HeaderPresenter = ({
  onBack,
  pageView,
  addBottomMargin = true,
  clientName,
  setApplicationError,
  hideCloseButton,
  hideLogo = false,
}: HeaderPresenterProps) => {
  const { activeTheme, loadingState, data } = useConfiguration();
  const { featureFlags } = data;
  const { tinklinkAnalytics } = useAnalytics();
  const { productType } = useProduct();
  const market = applicationParameters.get('market');
  const {
    header: { showBackButton, showCloseButton, logoAlign },
  } = activeTheme;

  // Check if file or URL exists. Prioritize file over URL
  const modifiedFileName = `data:image/png;base64,`;
  const { file, url } = activeTheme.logo;
  let clientLogo = '';

  if (market === MarketCode.US) {
    clientLogo = TINK_LOGO;
  } else {
    clientLogo = file ? `${modifiedFileName}${file.replace(modifiedFileName, '')}` : url;
  }

  const onCloseWithAnalytics = async () => {
    await tinklinkAnalytics.sendInteractionEvent({
      action: `${pageView}/${UserInteractionEvent.CLOSE_BUTTON}`,
      view: pageView,
    });

    parentMessenger.fireInteractionEvent({
      pageView,
      element: InteractionElement.CLOSE_BUTTON,
    });
    setApplicationError(ERR_USER_CANCELLED);
  };
  const onBackWithAnalytics = onBack
    ? () => {
        tinklinkAnalytics.sendInteractionEvent({
          action: `${pageView}/${UserInteractionEvent.BACK_BUTTON}`,
          view: pageView,
        });

        parentMessenger.fireInteractionEvent({
          pageView,
          element: InteractionElement.BACK_BUTTON,
        });
        onBack();
      }
    : undefined;

  const { isNativeNavigation } = useUrlQuery();

  if (isNativeNavigation) {
    // allow mobile-sdk to hide away navbar while rendering page in the webview..
    return <HiddenHeader data-testid="hidden-header" />;
  }

  const isLoading = loadingState === ConfigurationLoadingState.LOADING;

  if (isLoading) {
    return (
      <StyledLoadingHeader data-testid="loading-header" addBottomMargin={addBottomMargin}>
        <SkeletonLineLoader height="20px" width="100px" />
      </StyledLoadingHeader>
    );
  }

  // This feature_flag name is confusing as it hides both header buttons and not only the back button as it says.
  // Because this is used by several apps and is recorded in the DB under this FF we can't change the name.
  const showHeaderButtons = !featureFlags.includes(
    ConfigurationFeatureFlag.REDIRECT_BACK_TO_CLIENT_BUTTON_HIDDEN
  );

  const renderCloseButton = showHeaderButtons && showCloseButton && !hideCloseButton;
  // it is a hack, but we do not have any other way to avoid massive changes
  const hideGoBackForPayments =
    onBack?.name === hideGoBackButtonFunctionHackName && productType === ProductType.PAY;
  const renderBackButton =
    showHeaderButtons && showBackButton && Boolean(onBack) && !hideGoBackForPayments;

  return (
    <HeaderMolecule
      onBack={renderBackButton ? onBackWithAnalytics : undefined}
      onClose={renderCloseButton ? onCloseWithAnalytics : undefined}
      clientLogo={clientLogo}
      hideLogo={hideLogo}
      clientName={clientName}
      addBottomMargin={addBottomMargin}
      logoAlign={logoAlign}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return { clientName: state.oauth.description?.clientName };
};

const mapDispatchToProps = {
  setApplicationError: setApplicationErrorConnect,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const Header = connector(HeaderPresenter);
